import { useState } from 'react';
import { Badge, Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui';
import { Skeleton } from '@/components/custom';
import { EventTypeEnum } from '@/enums';
import { cn, getPhotoUrl } from '@/lib/utils';
import { EVENT_STATUS_CONFIG } from '@/constants';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Calendar, Users, Settings } from 'lucide-react';
import type { IEvent } from '@/interfaces';
import EventPrice from './EventPrice';

const formatDateTime = (d: string) => {
	try {
		return format(new Date(d), 'dd MMMM yyyy à HH:mm', { locale: fr });
	} catch {
		return d;
	}
};

type TRow = { label: string; value: React.ReactNode };

const DetailRow = ({ label, value }: TRow) => (
	<div className="flex justify-between items-center gap-2">
		<span className="text-sm text-muted-foreground whitespace-nowrap">{label}</span>
		<span className="text-sm font-medium text-right">{value}</span>
	</div>
);

const EventImage = ({ src }: { src: string }) => {
	const [loaded, setLoaded] = useState(false);
	return (
		<div className="relative aspect-video w-full overflow-hidden rounded-xl border bg-muted/20">
			{!loaded && <Skeleton className="absolute inset-0 w-full h-full rounded-none" />}
			<img
				src={src}
				alt="event banner"
				className={cn('w-full h-full object-cover', !loaded && 'invisible')}
				onLoad={() => setLoaded(true)}
			/>
		</div>
	);
};

type TProps = {
	event: IEvent | null;
	isOpen: boolean;
	onClose: () => void;
};


const getTypeBadge = (type: EventTypeEnum) => {
	const labels: Record<EventTypeEnum, string> = {
		[EventTypeEnum.BLIND_DATING]: 'Blind Dating',
	};
	return <Badge variant="outline">{labels[type]}</Badge>;
};

const EventDetailsModal = ({ event, isOpen, onClose }: TProps) => {
	if (!event) return null;

	return (
		<Dialog open={isOpen} onOpenChange={onClose}>
			<DialogContent
				showCloseIcon
				onClickCloseIcon={onClose}
				className="flex flex-col p-0 gap-0 max-w-lg w-[calc(100%-1.5rem)] sm:w-full lg:max-w-6xl max-h-[90vh] rounded-2xl border bg-background shadow-lg overflow-hidden"
			>
				<DialogHeader className="px-6 pt-6 pb-0">
					<DialogTitle className="flex items-center gap-2 text-xl">
						<Calendar className="h-5 w-5 text-primary" />
						Détails de l'événement
					</DialogTitle>
				</DialogHeader>

				<div className="px-6 py-6 overflow-y-auto">
					<div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
						{/* Configuration & Details Column */}
						<div className="flex flex-col gap-6">
							{/* Infos principales */}
							<div className="space-y-3">
								<DetailRow
									label="Type"
									value={getTypeBadge(event.type)}
								/>
								<DetailRow
									label="Statut"
									value={
									<Badge className={cn('text-xs font-medium', EVENT_STATUS_CONFIG[event.status].className)}>
										{EVENT_STATUS_CONFIG[event.status].label}
									</Badge>
								}
								/>
								<DetailRow label="Début" value={formatDateTime(event.startsAt)} />
								<DetailRow label="Fin" value={formatDateTime(event.endsAt)} />
								<DetailRow label="Prix" value={<EventPrice price={event.price} />} />
								
								{/* Titre & Description bilingues */}
								{(event.title || event.description) && (
									<div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
										{(['fr', 'en'] as const).map(lang => {
											const title = event.title?.[lang];
											const desc = event.description?.[lang];
											if (!title && !desc) return null;
											return (
												<div key={lang} className="flex flex-col gap-1.5 rounded-lg border bg-muted/20 px-3 py-2 min-w-0">
													<span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{lang}</span>
													{title && <span className="text-sm font-medium wrap-break-word">{title}</span>}
													{desc && <span className="text-xs text-muted-foreground wrap-break-word">{desc}</span>}
												</div>
											);
										})}
									</div>
								)}
							</div>

							<div className="border-t" />

							{/* Blind Dating Config */}
							{event.type === EventTypeEnum.BLIND_DATING && (
								<div className="space-y-3">
									<div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
										<Settings className="h-3.5 w-3.5" />
										Configuration Blind Dating
									</div>
									<div className="flex flex-col gap-3 p-4 border rounded-lg bg-muted/30">
										<DetailRow label="Durée d'un round" value={`${event.config.roundDurationMinutes} min`} />
										<DetailRow label="Marge d'âge" value={`${event.config.ageBufferYears} ans`} />
										<DetailRow label="Places" value={event.maxParticipants ?? 'Illimité'} />
									</div>
								</div>
							)}
						</div>

						{/* Visual Column */}
						<div className="flex flex-col gap-4 h-fit">
							<div className="flex flex-col gap-1 w-full">
								<span className="text-sm text-muted-foreground mb-1">Bannière de l'événement</span>
								<EventImage key={event._id} src={getPhotoUrl(event.bannerImage)} />
							</div>

							{/* Métriques */}
							<div className="space-y-3">
								<div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
									<Users className="h-3.5 w-3.5" />
									Chiffres
								</div>
								<div className="grid grid-cols-3 gap-4">
									{[
										{ label: 'Inscrits', value: event.metrics.registeredCount },
										{ label: 'Rounds', value: event.metrics.roundsCount },
										{ label: 'Matchs', value: event.metrics.matchesCount },
									].map(metric => (
										<div key={metric.label} className="flex flex-col gap-1 p-3 border rounded-lg bg-muted/20">
											<p className="text-xs text-muted-foreground">{metric.label}</p>
											<p className="text-2xl font-semibold">{metric.value}</p>
										</div>
									))}
								</div>
							</div>
						</div>
					</div>
				</div>

			</DialogContent>
		</Dialog>
	);
};

export default EventDetailsModal;
